import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchCompanies,
  fetchConsolidatedForecast,
  fetchForecast,
  recalculateBpBehavior,
  type Company,
  type ConsolidatedForecastResult,
  type ForecastBpDelaysInfo,
  type ForecastGranularity,
  type ForecastPeriod,
  type ForecastResult,
  type ForecastScenario,
} from "../api";
import { formatDateEs, formatMoney } from "../lib/grouping";

/**
 * Previsión de tesorería con layout MATRIZ conceptos × periodos:
 *
 *   Empresa        | Ene | Feb | ... | Dic
 *   Ingresos       (header verde)
 *     Facturas Clientes | ...
 *     Efectos Clientes  | ...
 *     Recurrentes       | ...
 *     Total ingresos    (subtotal)
 *   Pagos          (header rojo)
 *     Facturas Proveedores | ...
 *     Efectos Proveedores  | ...
 *     Gastos Financieros (header naranja)
 *       Leasing        | ...
 *       Avales         | ...
 *       Prestamos      | ...
 *     Recurrentes        | ...
 *     Total Pagos        (subtotal)
 *   Total Saldo    (banda azul; celdas negativas en rojo)
 */

const SCENARIO_LABEL: Record<ForecastScenario, string> = {
  realistic: "Realista",
  pessimistic: "Pesimista (clientes tarde)",
  optimistic: "Optimista (retener caja)",
};

type Mode = "company" | "consolidated";

type UnifiedResult = {
  currency: string;
  generatedAt: string;
  horizonMonths: number;
  granularity: ForecastGranularity;
  scenario: ForecastScenario;
  startingBalance: number;
  periods: ForecastPeriod[];
  warnings: string[];
  bpDelays?: ForecastBpDelaysInfo;
  companies?: Array<{ id: string; name: string; alias: string }>;
  failures?: Array<{ companyId: string; message: string }>;
  bpDelaysCompaniesUsed?: number;
};

// ─── Paleta (verde ingresos / rojo pagos / naranja gastos fin. / azul saldo) ─
const C = {
  green: { bg: "#e8f5e9", text: "#1b5e20", strong: "#2e7d32", cellBg: "#f1f8f2" },
  red: { bg: "#ffebee", text: "#b71c1c", strong: "#c62828", cellBg: "#fdf3f4" },
  orange: { bg: "#fff3e0", text: "#e65100", strong: "#ef6c00" },
  blue: { bg: "#e3f2fd", text: "#0d47a1", strong: "#1565c0", negBg: "#ffcdd2", negText: "#b71c1c" },
  neutral: { bg: "#f5f5f5", border: "#e0e0e0", label: "#555" },
};

export function Forecast() {
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [mode, setMode] = useState<Mode>("company");
  const [companyId, setCompanyId] = useState<string>("");
  const [months, setMonths] = useState<number>(12);
  const [scenario, setScenario] = useState<ForecastScenario>("realistic");
  const [useHistoricalDelays, setUseHistoricalDelays] = useState<boolean>(true);
  const [result, setResult] = useState<UnifiedResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [recalcing, setRecalcing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fetchCompanies()
      .then((r) => {
        setCompanies(r.items);
        const withSap = r.items.find((c) => c.sap !== null);
        setCompanyId((withSap ?? r.items[0])?.id ?? "");
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  const runForecast = async () => {
    if (mode === "company" && !companyId) return;
    setLoading(true);
    setError(null);
    try {
      // Granularidad SIEMPRE mensual en el layout matriz (una columna por mes).
      const granularity: ForecastGranularity = "month";
      if (mode === "consolidated") {
        const r: ConsolidatedForecastResult = await fetchConsolidatedForecast({
          months,
          granularity,
          scenario,
          useHistoricalDelays,
        });
        setResult({
          currency: r.currency,
          generatedAt: r.generatedAt,
          horizonMonths: r.horizonMonths,
          granularity: r.granularity,
          scenario: r.scenario,
          startingBalance: r.startingBalance,
          periods: r.periods,
          warnings: r.warnings,
          companies: r.companies,
          failures: r.failures,
          bpDelaysCompaniesUsed: r.bpDelaysCompaniesUsed,
        });
      } else {
        const r: ForecastResult = await fetchForecast(companyId, {
          months,
          granularity,
          scenario,
          useHistoricalDelays,
        });
        setResult({
          currency: r.currency,
          generatedAt: r.generatedAt,
          horizonMonths: r.horizonMonths,
          granularity: r.granularity,
          scenario: r.scenario,
          startingBalance: r.startingBalance,
          periods: r.periods,
          warnings: r.warnings,
          bpDelays: r.bpDelays,
        });
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void runForecast();

  }, [mode, companyId, months, scenario, useHistoricalDelays]);

  const recalcBpBehavior = async () => {
    if (mode !== "company" || !companyId) return;
    setRecalcing(true);
    setError(null);
    try {
      const r = await recalculateBpBehavior(companyId);
      alert(
        `Recálculo OK\n${r.customerBps} cliente(s) con histórico · ${r.supplierBps} proveedor(es)\n` +
          `Facturas leídas: ${r.invoicesScanned} OINV + ${r.purchaseInvoicesScanned} OPCH`,
      );
      void runForecast();
    } catch (e: unknown) {
      alert(`Error al recalcular: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRecalcing(false);
    }
  };

  const currency = result?.currency ?? "EUR";
  const periods = result?.periods ?? [];
  const scopeLabel = useMemo(() => {
    if (mode === "consolidated") return "Consolidado";
    const c = companies?.find((x) => x.id === companyId);
    return c ? c.alias || c.name : companyId;
  }, [companies, companyId, mode]);

  // ─── Serie por concepto (una fila = un array de N periodos) ────────────
  const rows = useMemo(() => {
    if (periods.length === 0) return null;
    const map = (fn: (p: ForecastPeriod) => number): number[] => periods.map(fn);
    return {
      // Ingresos
      inv_in: map((p) => p.inflows.invoices),
      boe_in: map((p) => p.inflows.boe),
      rec_in: map((p) => p.inflows.recurring),
      tot_in: map((p) => p.inflows.total),
      // Pagos
      inv_out: map((p) => p.outflows.invoices),
      boe_out: map((p) => p.outflows.boe),
      leas_out: map((p) => p.outflows.leasings),
      aval_out: map((p) => p.outflows.avales),
      loan_out: map((p) => p.outflows.loans),
      rec_out: map((p) => p.outflows.recurring),
      tot_out: map((p) => p.outflows.total),
      // Saldo final por periodo (cadena)
      saldo: map((p) => p.closingBalance),
    };
  }, [periods]);

  const exportCsv = () => {
    if (!result || !rows) return;
    const monthCols = periods.map((p) => p.label);
    const header = ["Concepto", ...monthCols].map(csvCell).join(";");
    const rowLines: string[] = [];
    const push = (label: string, arr: number[]) => {
      rowLines.push([label, ...arr.map((n) => n.toFixed(2))].map(csvCell).join(";"));
    };
    rowLines.push(["INGRESOS", ...monthCols.map(() => "")].map(csvCell).join(";"));
    push("Facturas Clientes", rows.inv_in);
    push("Efectos Clientes", rows.boe_in);
    push("Recurrentes", rows.rec_in);
    push("Total ingresos", rows.tot_in);
    rowLines.push(["PAGOS", ...monthCols.map(() => "")].map(csvCell).join(";"));
    push("Facturas Proveedores", rows.inv_out);
    push("Efectos Proveedores", rows.boe_out);
    rowLines.push(["  Gastos Financieros", ...monthCols.map(() => "")].map(csvCell).join(";"));
    push("  Leasing", rows.leas_out);
    push("  Avales", rows.aval_out);
    push("  Prestamos", rows.loan_out);
    push("Recurrentes", rows.rec_out);
    push("Total Pagos", rows.tot_out);
    push("Total Saldo", rows.saldo);
    const csv = "﻿" + [header, ...rowLines].join("\r\n");
    const scopeTag = mode === "consolidated" ? "consolidado" : companyId;
    const filename = `prevision_${scopeTag}_${result.scenario}_${result.horizonMonths}m.csv`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Previsión de tesorería</h2>
          <p className="meta">
            Ingresos y pagos previstos por mes. Fuentes: SAP (facturas y efectos abiertos),
            préstamos, leasings, avales, conceptos recurrentes. Los vencidos caen en el primer mes.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ display: "flex", gap: "0.25rem" }}>
            <button
              type="button"
              onClick={() => setMode("company")}
              disabled={mode === "company"}
              style={mode === "company" ? { fontWeight: 600 } : undefined}
            >
              Por empresa
            </button>
            <button
              type="button"
              onClick={() => setMode("consolidated")}
              disabled={mode === "consolidated"}
              style={mode === "consolidated" ? { fontWeight: 600 } : undefined}
            >
              Consolidado
            </button>
          </div>
          {mode === "company" && (
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} disabled={!companies}>
              <option value="">— Empresa —</option>
              {companies?.map((c) => (
                <option key={c.id} value={c.id}>
                  {(c.alias || c.name) + (c.sap ? "" : " (sin SAP)")} · {c.id}
                </option>
              ))}
            </select>
          )}
          <select value={scenario} onChange={(e) => setScenario(e.target.value as ForecastScenario)}>
            {(Object.keys(SCENARIO_LABEL) as ForecastScenario[]).map((s) => (
              <option key={s} value={s}>{SCENARIO_LABEL[s]}</option>
            ))}
          </select>
          <select value={months} onChange={(e) => setMonths(Number(e.target.value))}>
            <option value={3}>3 meses</option>
            <option value={6}>6 meses</option>
            <option value={12}>12 meses</option>
          </select>
          <button type="button" onClick={runForecast} disabled={loading}>
            {loading ? "Calculando…" : "Recalcular"}
          </button>
          <button type="button" onClick={exportCsv} disabled={!result}>Exportar CSV</button>
          <Link to="/conceptos-recurrentes" className="side-link" style={{ padding: "0.35rem 0.7rem" }}>
            Recurrentes →
          </Link>
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "center", marginTop: "0.5rem" }}>
        <label style={{ display: "flex", gap: "0.35rem", alignItems: "center", fontSize: "0.85rem" }}>
          <input
            type="checkbox"
            checked={useHistoricalDelays}
            onChange={(e) => setUseHistoricalDelays(e.target.checked)}
          />
          Retraso histórico por tercero (mediana 12m)
        </label>
        {mode === "company" && (
          <button type="button" onClick={recalcBpBehavior} disabled={!companyId || recalcing}>
            {recalcing ? "Recalculando…" : "Recalcular comportamientos"}
          </button>
        )}
        {mode === "company" && result?.bpDelays && <BpDelaysPill info={result.bpDelays} />}
        {mode === "consolidated" && result && (
          <span className="meta" style={{ fontSize: "0.8rem" }}>
            {result.bpDelaysCompaniesUsed ?? 0} de {result.companies?.length ?? 0} empresas con histórico
          </span>
        )}
      </div>

      {error && <p className="error">{error}</p>}
      {!companies && !error && <p>Cargando empresas…</p>}
      {companies && companies.length === 0 && (
        <p className="warning">
          No hay empresas dadas de alta. Crea una en "Empresas" para poder previsionar.
        </p>
      )}

      {result && result.failures && result.failures.length > 0 && (
        <div className="error" style={{ marginTop: "0.75rem" }}>
          <strong>Empresas con error en el consolidado:</strong>
          <ul style={{ margin: "0.25rem 0 0 1.25rem" }}>
            {result.failures.map((f, i) => <li key={i}><b>{f.companyId}</b>: {f.message}</li>)}
          </ul>
        </div>
      )}
      {result && result.warnings.length > 0 && (
        <div className="warning" style={{ marginTop: "0.75rem" }}>
          <strong>Avisos:</strong>
          <ul style={{ margin: "0.25rem 0 0 1.25rem" }}>
            {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {result && rows && (
        <div style={{ marginTop: "1rem", overflowX: "auto" }}>
          <ForecastMatrix
            scopeLabel={scopeLabel}
            currency={currency}
            periods={periods}
            rows={rows}
          />
          <p className="meta" style={{ marginTop: "0.75rem" }}>
            Generado {formatDateEs(result.generatedAt.slice(0, 10))} · Moneda: {currency} ·
            Saldo inicial: {formatMoney(result.startingBalance, currency)}
          </p>
        </div>
      )}
    </section>
  );
}

// ─── Matriz ──────────────────────────────────────────────────────────────────

type MatrixRows = {
  inv_in: number[];
  boe_in: number[];
  rec_in: number[];
  tot_in: number[];
  inv_out: number[];
  boe_out: number[];
  leas_out: number[];
  aval_out: number[];
  loan_out: number[];
  rec_out: number[];
  tot_out: number[];
  saldo: number[];
};

function ForecastMatrix(props: {
  scopeLabel: string;
  currency: string;
  periods: ForecastPeriod[];
  rows: MatrixRows;
}) {
  const { scopeLabel, currency, periods, rows } = props;
  return (
    <table
      style={{
        borderCollapse: "collapse",
        width: "100%",
        fontSize: "0.85rem",
        minWidth: "900px",
      }}
    >
      <thead>
        <tr>
          <th style={{ ...thScope, background: C.neutral.bg }}>Empresa</th>
          <th style={{ ...thScope, background: C.neutral.bg, color: C.neutral.label }}>
            {scopeLabel}
          </th>
          {periods.map((p) => (
            <th
              key={p.from}
              style={{
                ...thMonth,
                background: C.neutral.bg,
                color: C.neutral.label,
              }}
            >
              {monthLabel(p.label, p.from)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {/* INGRESOS */}
        <GroupHeaderRow label="Ingresos" bg={C.green.bg} color={C.green.text} span={periods.length + 2} />
        <DataRow label="Facturas Clientes" values={rows.inv_in} currency={currency} tint={C.green.cellBg} />
        <DataRow label="Efectos Clientes" values={rows.boe_in} currency={currency} tint={C.green.cellBg} />
        <DataRow label="Recurrentes" values={rows.rec_in} currency={currency} tint={C.green.cellBg} />
        <SubtotalRow
          leftLabel=""
          middleLabel="Total ingresos"
          values={rows.tot_in}
          currency={currency}
          bg={C.green.bg}
          color={C.green.strong}
        />

        {/* PAGOS */}
        <GroupHeaderRow label="Pagos" bg={C.red.bg} color={C.red.text} span={periods.length + 2} />
        <DataRow label="Facturas Proveedores" values={rows.inv_out} currency={currency} tint={C.red.cellBg} />
        <DataRow label="Efectos Proveedores" values={rows.boe_out} currency={currency} tint={C.red.cellBg} />
        {/* Sub-grupo Gastos Financieros */}
        <SubGroupHeaderRow label="Gastos Financieros" bg={C.orange.bg} color={C.orange.text} span={periods.length + 2} />
        <DataRow label="Leasing" values={rows.leas_out} currency={currency} indent tint={C.red.cellBg} />
        <DataRow label="Avales" values={rows.aval_out} currency={currency} indent tint={C.red.cellBg} />
        <DataRow label="Prestamos" values={rows.loan_out} currency={currency} indent tint={C.red.cellBg} />
        <DataRow label="Recurrentes" values={rows.rec_out} currency={currency} tint={C.red.cellBg} />
        <SubtotalRow
          leftLabel=""
          middleLabel="Total Pagos"
          values={rows.tot_out}
          currency={currency}
          bg={C.red.bg}
          color={C.red.strong}
        />

        {/* TOTAL SALDO */}
        <SaldoRow values={rows.saldo} currency={currency} />
      </tbody>
    </table>
  );
}

function GroupHeaderRow(props: { label: string; bg: string; color: string; span: number }) {
  return (
    <tr>
      <td
        colSpan={props.span}
        style={{
          background: props.bg,
          color: props.color,
          padding: "0.45rem 0.6rem",
          fontWeight: 700,
          fontSize: "0.9rem",
          borderTop: `1px solid ${C.neutral.border}`,
          borderBottom: `1px solid ${C.neutral.border}`,
          letterSpacing: "0.02em",
          textTransform: "uppercase",
        }}
      >
        {props.label}
      </td>
    </tr>
  );
}

function SubGroupHeaderRow(props: { label: string; bg: string; color: string; span: number }) {
  return (
    <tr>
      <td
        colSpan={props.span}
        style={{
          background: props.bg,
          color: props.color,
          padding: "0.3rem 0.6rem 0.3rem 1.2rem",
          fontWeight: 600,
          fontSize: "0.8rem",
        }}
      >
        {props.label}
      </td>
    </tr>
  );
}

function DataRow(props: {
  label: string;
  values: number[];
  currency: string;
  indent?: boolean;
  tint?: string;
}) {
  return (
    <tr>
      <td
        style={{
          ...tdLabel,
          paddingLeft: props.indent ? "2rem" : "0.6rem",
          background: props.tint,
        }}
      >
        {props.label}
      </td>
      <td style={{ ...tdLabel, background: props.tint }}></td>
      {props.values.map((v, i) => (
        <td key={i} style={{ ...tdNum, background: props.tint }}>
          {formatCell(v, props.currency)}
        </td>
      ))}
    </tr>
  );
}

function SubtotalRow(props: {
  leftLabel: string;
  middleLabel: string;
  values: number[];
  currency: string;
  bg: string;
  color: string;
}) {
  return (
    <tr>
      <td
        style={{
          ...tdLabel,
          background: props.bg,
          color: props.color,
          fontWeight: 600,
        }}
      >
        {props.leftLabel}
      </td>
      <td
        style={{
          ...tdLabel,
          background: props.bg,
          color: props.color,
          fontWeight: 700,
        }}
      >
        {props.middleLabel}
      </td>
      {props.values.map((v, i) => (
        <td
          key={i}
          style={{
            ...tdNum,
            background: props.bg,
            color: props.color,
            fontWeight: 700,
          }}
        >
          {formatCell(v, props.currency)}
        </td>
      ))}
    </tr>
  );
}

function SaldoRow(props: { values: number[]; currency: string }) {
  return (
    <tr>
      <td
        style={{
          ...tdLabel,
          background: C.blue.bg,
          color: C.blue.text,
          fontWeight: 700,
        }}
      ></td>
      <td
        style={{
          ...tdLabel,
          background: C.blue.bg,
          color: C.blue.text,
          fontWeight: 800,
        }}
      >
        Total Saldo
      </td>
      {props.values.map((v, i) => {
        const neg = v < 0;
        return (
          <td
            key={i}
            style={{
              ...tdNum,
              background: neg ? C.blue.negBg : C.blue.bg,
              color: neg ? C.blue.negText : C.blue.strong,
              fontWeight: 800,
            }}
          >
            {formatCell(v, props.currency)}
          </td>
        );
      })}
    </tr>
  );
}

// ─── Estilos de celda base ──────────────────────────────────────────────────

const thScope: React.CSSProperties = {
  padding: "0.5rem 0.6rem",
  textAlign: "left",
  borderBottom: `1px solid ${C.neutral.border}`,
  fontWeight: 700,
  fontSize: "0.82rem",
  position: "sticky",
  top: 0,
};
const thMonth: React.CSSProperties = {
  ...thScope,
  textAlign: "right",
  fontWeight: 600,
  fontSize: "0.75rem",
  textTransform: "capitalize",
};
const tdLabel: React.CSSProperties = {
  padding: "0.3rem 0.6rem",
  borderBottom: `1px solid ${C.neutral.border}`,
  whiteSpace: "nowrap",
};
const tdNum: React.CSSProperties = {
  padding: "0.3rem 0.6rem",
  borderBottom: `1px solid ${C.neutral.border}`,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
};

// ─── Utilidades ─────────────────────────────────────────────────────────────

/** Muestra "—" en vez de "0,00 €" para no saturar visualmente la matriz. */
function formatCell(v: number, currency: string): string {
  if (!Number.isFinite(v) || Math.abs(v) < 0.005) return "—";
  return formatMoney(v, currency);
}

/**
 * Etiqueta del mes en la cabecera. Cuando `label` viene tipo "2026-07"
 * devuelve "Julio 26"; para semanales cae al label original.
 */
function monthLabel(label: string, from: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(label);
  if (!m) return label;
  const meses = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];
  const mi = Number(m[2]) - 1;
  const yy = m[1]!.slice(2);
  void from;
  return `${meses[mi] ?? label} ${yy}`;
}

function csvCell(value: string): string {
  if (/[";\r\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

function BpDelaysPill(props: { info: ForecastBpDelaysInfo }) {
  const { info } = props;
  const bg = info.used ? "#eaf7ec" : "#fff4dc";
  const fg = info.used ? "#2f9e44" : "#a85d00";
  const label = info.used
    ? `Histórico aplicado · ${info.customerBpsWithHistory} cliente(s) · ${info.supplierBpsWithHistory} proveedor(es)`
    : "Sin caché (pulsa Recalcular)";
  return (
    <span
      style={{
        padding: "0.2rem 0.6rem",
        borderRadius: "999px",
        background: bg,
        color: fg,
        fontSize: "0.75rem",
        fontWeight: 500,
      }}
      title={info.computedAt ? `Calculado el ${formatDateEs(info.computedAt.slice(0, 10))}` : "Sin datos"}
    >
      {label}
    </span>
  );
}
